/* eslint-disable @typescript-eslint/no-unused-vars */
import { FormikProps } from "formik";
import Spinner from "../Spinner";
import styles from "./styles.module.scss";
import { SelectOption, WisaTabletFormInitValues } from "../../interfaces/fe.interfaces";
import { handleAlphaNumInputChange } from "../../utils/helpers";
import Select from "../Select";
import { getCustomers } from "../../api/config";
import { useFetch } from "../../hooks/useFetch";
import { GetCustomersResponse } from "../../interfaces/be.interfaces";
import { useState } from "react";
import { Icons } from "../../../assets/images";
import moment from "moment";

interface FormProps extends FormikProps<WisaTabletFormInitValues> {
  editMode?: boolean;
  onCancelClick: () => void;
}

const Form = ({
  values,
  touched,
  setFieldTouched,
  errors,
  handleSubmit,
  onCancelClick,
  setFieldValue,
  isSubmitting,
  editMode,
}: FormProps) => {
  const [customerList, setCustomerList] = useState<SelectOption<string | null>[]>([]);
  const [openAccordions, setOpenAccordions] = useState<string[]>(["general"]);

  // Fetch list of customers
  useFetch<GetCustomersResponse>({
    runOnMount: true,
    url: getCustomers,
    onSuccess: (response) => {
      // Prepare customer data in option format to show in dropdown and filter inactive customers
      const customerOptions: SelectOption<string>[] = response.data?.data?.customers
        ?.filter((item) => item.is_enable)
        .map((customer) => ({
          label: customer.customer_name,
          value: customer.vendor_id,
        }));

      setCustomerList(customerOptions);
    },
  });

  // Accordion toggle handler
  const handleToggleAccordion = (id: string) => {
    if (openAccordions.includes(id)) {
      setOpenAccordions((ids) => ids.filter((v) => v !== id));
    } else {
      setOpenAccordions((ids) => [...ids, id]);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.dialog}>
        {isSubmitting ? (
          <Spinner />
        ) : (
          <>
            <div className={styles.sectionContainer}>
              <p className={styles.sectionTitle}>
                General Information
                <Icons.ChevronLeft
                  className={`${styles.arrow} ${openAccordions.includes("general") ? styles.active : ""}`}
                  onClick={() => {
                    handleToggleAccordion("general");
                  }}
                />
              </p>
              {openAccordions.includes("general") && (
                <>
                  <div className={styles.inputContainer}>
                    <div className={styles.inputItem}>
                      <label htmlFor="it_tag">IT Tag</label>
                      <input
                        name="it_tag"
                        type="text"
                        placeholder={"Enter IT Tag"}
                        value={values.it_tag}
                        onChange={(e) => handleAlphaNumInputChange(e, setFieldValue)}
                        onBlur={() => setFieldTouched("it_tag", true)}
                      />
                      {errors.it_tag && touched.it_tag && <p className={styles.error}>{errors.it_tag}</p>}
                    </div>
                    <div className={styles.inputItem}>
                      <label htmlFor="tablet_device_id">Device ID</label>
                      <input
                        name="tablet_device_id"
                        type="text"
                        placeholder={"Enter Tablet Device ID"}
                        value={values.tablet_device_id}
                        onChange={(e) => handleAlphaNumInputChange(e, setFieldValue)}
                        onBlur={() => setFieldTouched("tablet_device_id", true)}
                      />
                      {errors.tablet_device_id && touched.tablet_device_id && <p className={styles.error}>{errors.tablet_device_id}</p>}
                    </div>
                  </div>
                  <div className={styles.inputContainer}>
                    <div className={styles.inputItem}>
                      <label htmlFor="tablet_name">Tablet Name</label>
                      <input
                        name="tablet_name"
                        type="text"
                        placeholder={"Enter Tablet Name"}
                        value={values.tablet_name}
                        onChange={(e) => handleAlphaNumInputChange(e, setFieldValue)}
                        onBlur={() => setFieldTouched("tablet_name", true)}
                      />
                      {errors.tablet_name && touched.tablet_name && <p className={styles.error}>{errors.tablet_name}</p>}
                    </div>
                    <div className={styles.inputItem}>
                      <label htmlFor="tablet_mfg">Tablet Manufacturer</label>
                      <input
                        name="tablet_mfg"
                        type="text"
                        placeholder={"Enter Tablet Mfg"}
                        value={values.tablet_mfg}
                        onChange={(e) => handleAlphaNumInputChange(e, setFieldValue)}
                        onBlur={() => setFieldTouched("tablet_mfg", true)}
                      />
                      {errors.tablet_mfg && touched.tablet_mfg && <p className={styles.error}>{errors.tablet_mfg}</p>}
                    </div>
                  </div>
                  <div className={styles.inputContainer}>
                    <div className={styles.inputItem}>
                      <div className={styles.dateContainer}>
                        <label htmlFor="return_date">Return Date</label>
                        <input
                          name="return_date"
                          type="date"
                          value={values.return_date?.toString() ?? ""}
                          onChange={(e) => setFieldValue("return_date", e.target.value)}
                          onBlur={() => setFieldTouched("return_date", true)}
                        />
                      </div>

                      {errors.return_date && touched.return_date && <p className={styles.error}>{errors.return_date}</p>}
                    </div>
                    <div className={styles.inputItem}>
                      <label htmlFor="customer_checked_out_to">Assign Customer</label>
                      <Select
                        selectProps={{
                          value: values.customer_checked_out_to ?? "",
                          name: "customer_checked_out_to",
                          placeholder: "Select Customer",
                          onChange: (event) => setFieldValue("customer_checked_out_to", event.target.value),
                        }}
                        options={customerList}
                      />
                      {errors?.customer_checked_out_to && touched?.customer_checked_out_to ? (
                        <p className={styles.error}>{errors.customer_checked_out_to}</p>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={styles.sectionContainer}>
              <p className={styles.sectionTitle}>
                Calibration Information
                <Icons.ChevronLeft
                  className={`${styles.arrow} ${openAccordions.includes("calibration") ? styles.active : ""}`}
                  onClick={() => {
                    handleToggleAccordion("calibration");
                  }}
                />
              </p>
              {openAccordions.includes("calibration") && (
                <>
                  <div className={styles.inputContainer}>
                    <div className={styles.inputItem}>
                      <div className={styles.dateContainer}>
                        <label htmlFor="calibration_date">Calibration Date</label>
                        <input
                          name="calibration_date"
                          type="date"
                          value={values.calibration_date?.toString() ?? ""}
                          onChange={(e) => {
                            setFieldValue("calibration_date", e.target.value);
                            setFieldValue("return_date", moment(e.target.value).add(180, "d").format("YYYY-MM-DD"));
                          }}
                          onBlur={() => setFieldTouched("calibration_date", true)}
                        />
                      </div>

                      {errors.calibration_date && touched.calibration_date && <p className={styles.error}>{errors.calibration_date}</p>}
                    </div>
                    <div className={styles.inputItem}>
                      <label htmlFor="rssi">RSSI</label>
                      <input
                        name="rssi"
                        type="number"
                        placeholder={"RSSI Value"}
                        value={values.rssi ?? ""}
                        onChange={(e) => handleAlphaNumInputChange(e, setFieldValue)}
                        onBlur={() => setFieldTouched("rssi", true)}
                      />
                      {errors.rssi && touched.rssi && <p className={styles.error}>{errors.rssi}</p>}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={styles.sectionContainer}>
              <p className={styles.sectionTitle}>
                Shipping Information
                <Icons.ChevronLeft
                  className={`${styles.arrow} ${openAccordions.includes("shipping") ? styles.active : ""}`}
                  onClick={() => {
                    handleToggleAccordion("shipping");
                  }}
                />
              </p>
              {openAccordions.includes("shipping") && (
                <>
                  <div className={styles.inputContainer}>
                    <div className={styles.inputItem}>
                      <label htmlFor="ship_from_tracking_id">Ship From Tracking ID</label>
                      <input
                        name="ship_from_tracking_id"
                        type="number"
                        placeholder={"Ship from tracking ID"}
                        value={values.ship_from_tracking_id ?? ""}
                        onChange={(e) => handleAlphaNumInputChange(e, setFieldValue)}
                        onBlur={() => setFieldTouched("ship_from_tracking_id", true)}
                      />
                      {errors.ship_from_tracking_id && touched.ship_from_tracking_id && (
                        <p className={styles.error}>{errors.ship_from_tracking_id}</p>
                      )}
                    </div>
                    <div className={styles.inputItem}>
                      <label htmlFor="ship_to_tracking_id">Ship To Tracking ID</label>
                      <input
                        name="ship_to_tracking_id"
                        type="number"
                        placeholder={"Ship to tracking ID"}
                        value={values.ship_to_tracking_id ?? ""}
                        onChange={(e) => handleAlphaNumInputChange(e, setFieldValue)}
                        onBlur={() => setFieldTouched("ship_to_tracking_id", true)}
                      />
                      {errors.ship_to_tracking_id && touched.ship_to_tracking_id && (
                        <p className={styles.error}>{errors.ship_to_tracking_id}</p>
                      )}
                    </div>
                  </div>
                  <div className={styles.inputContainer}>
                    <div className={styles.inputItem}>
                      <div className={styles.dateContainer}>
                        <label htmlFor="ship_from_tracking_id">Date shipped</label>
                        <input
                          name="date_shipped"
                          type="date"
                          value={values.date_shipped?.toString() ?? ""}
                          onChange={(e) => setFieldValue("date_shipped", e.target.value)}
                          onBlur={() => setFieldTouched("date_shipped", true)}
                        />
                      </div>
                      {errors.date_shipped && touched.date_shipped && <p className={styles.error}>{errors.date_shipped}</p>}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="w-100 d-flex justify-content-around">
              <button className={styles.dialogBtn} type="submit" disabled={isSubmitting}>
                {editMode ? "Update" : "Add"}
              </button>
              <button type="button" className={styles.dialogBtn} onClick={onCancelClick}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default Form;
